import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, deleteUser } from 'firebase/auth';
import { FaInstagram, FaFacebook, FaSoundcloud, FaSpotify } from 'react-icons/fa';
import '../styles/Profile.css';
import XIcon from '../assets/x-icon.png'
import BandlabIcon from '../assets/bandlab-icon.png'

const genresList = ['Rock', 'Pop', 'Hip-Hop', 'Electronic', 'Jazz', 'Classical', 'Country', 'Blues'];

const Profile = () => {
  const [userData, setUserData] = useState({
    artistName: '',
    genres: [],
    bio: '',
    profilePicture: '',
    city: '',
    state: '',
    socialLinks: {
      twitter: '',
      instagram: '',
      facebook: '',
      soundcloud: '',
      spotify: '',
      bandlab: '',
    },
  });
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [plan, setPlan] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const response = await axios.get(
            `https://sync-api-troz.onrender.com/api/users/firebase/${user.uid}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setPlan(response.data.plan);
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
      }
    };

    fetchUserPlan();
  }, [auth.currentUser]);

  useEffect(() => {
    const fetchUserProfile = async (firebaseUid) => {
      try {
        const response = await axios.get(`https://sync-api-troz.onrender.com/api/users/firebase/${firebaseUid}`);
        const data = response.data;
        if (!data.socialLinks) {
          data.socialLinks = {
            twitter: '',
            instagram: '',
            facebook: '',
            soundcloud: '',
            bandlab: '',
          };
        }
        setUserData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserProfile(user.uid);
      } else {
        setLoading(false);
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
  };

  const handleSocialLinkChange = (platform, value) => {
    setUserData({
      ...userData,
      socialLinks: {
        ...userData.socialLinks,
        [platform]: value,
      },
    });
  };

  const handleFileChange = (e) => {
    setProfilePictureFile(e.target.files[0]);
  };

  const handleGenreClick = (genre) => {
    if (!userData.genres.includes(genre)) {
      setUserData({ ...userData, genres: [...userData.genres, genre] });
    }
  };

  const removeGenre = (genre) => {
    setUserData({ ...userData, genres: userData.genres.filter((g) => g !== genre) });
  };

  const validateSocialLinks = (links) => {
    const platformPatterns = {
      twitter: /^(https?:\/\/)?(www\.)?(twitter\.com|x\.com)\/[A-Za-z0-9_]+\/?(\?.*)?$/i,
      instagram: /^(https?:\/\/)?(www\.)?instagram\.com\/[A-Za-z0-9_.]+\/?(\?.*)?$/i,
      facebook: /^(https?:\/\/)?(www\.)?facebook\.com\/[A-Za-z0-9.]+\/?(\?.*)?$/i,
      soundcloud: /^(https?:\/\/)?(www\.|on\.)?soundcloud\.com\/[A-Za-z0-9_-]+\/?(\?.*)?$/i,
      spotify: /^(https?:\/\/)?(open\.)?spotify\.com\/(artist|user|album|track)\/[A-Za-z0-9]+\/?(\?.*)?$/i,
      bandlab: /^(https?:\/\/)?(www\.)?bandlab\.com\/[A-Za-z0-9_.]+\/?(\?.*)?$/i,
    };

    for (const platform in links) {
      const url = links[platform];
      if (url && !platformPatterns[platform].test(url)) {
        return `Please enter a valid ${platform.charAt(0).toUpperCase() + platform.slice(1)} URL.`;
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errorMessage = validateSocialLinks(userData.socialLinks);
    if (errorMessage) {
      window.alert(errorMessage);
      return;
    }

    const formData = new FormData();
    formData.append('artistName', userData.artistName);
    formData.append('bio', userData.bio);
    formData.append('city', userData.city);
    formData.append('state', userData.state);
    userData.genres.forEach((genre) => formData.append('genres[]', genre));
    formData.append('socialLinks', JSON.stringify(userData.socialLinks));

    if (profilePictureFile) {
      formData.append('profilePicture', profilePictureFile);
    }

    try {
      const user = auth.currentUser;
      if (user) {
        await axios.put(`https://sync-api-troz.onrender.com/api/users/firebase/${user.uid}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        window.alert('Profile updated successfully');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      window.alert('Failed to update profile. Please try again.');
    }
  };

  const handleCancelSubscription = async () => {
    setSubmitting(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      const response = await axios.post(
        'https://sync-api-troz.onrender.com/api/cancel-subscription',
        { firebaseUid: user.uid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        setPlan('freemium');
        window.alert('Your subscription has been canceled.');
        navigate('/home');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      window.alert(error.response?.data?.message || 'Failed to cancel subscription.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManageSubscription = () => {
    if (plan === 'premium') {
      if (window.confirm('Would you like to cancel your premium subscription?')) {
        handleCancelSubscription();
      }
    } else {
      if (window.confirm('Would you like to upgrade to a premium plan?')) {
        navigate('/subscription');
      }
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      const response = await axios.delete(
        `https://sync-api-troz.onrender.com/api/users/firebase/${user.uid}/delete-user`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status !== 200) {
        throw new Error('Failed to delete user data from server');
      }

      await deleteUser(user);
      window.alert('Your account and all associated data have been deleted.');
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/requires-recent-login') {
        window.alert('Please sign out and sign back in to delete your account, then try again.');
      } else {
        window.alert(error.response?.data?.message || 'Failed to delete account. Please try again.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone and will remove all your data.')) {
      handleDeleteAccount();
    }
  };

  const handleLogout = () => {
    auth.signOut().then(() => {
      navigate('/login');
    }).catch((error) => {
      console.error('Error logging out:', error);
    });
  };

  if (loading) {
    return <div className="profile-loading">Loading...</div>;
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div
          className="profile-banner"
          style={{
            backgroundColor: userData.profilePicture ? 'transparent' : '#000',
          }}
        >
          {userData.profilePicture && (
            <img
              src={userData.profilePicture}
              alt="Profile"
              className="profile-banner-image"
            />
          )}
          <div className="profile-banner-text">
            <h2 className="profile-artist-name">{userData.artistName}</h2>
          </div>
        </div>
        <div className="profile-bio-container">
          <div className="profile-bio-content">
            <h3>Biography:</h3>
            <h5 className="profile-banner-bio">{userData.bio}</h5>
          </div>
        </div>
        <h1 className="profile-update-header">Update Profile</h1>
        <div className="profile-social-links">
          <div className="profile-social-row">
            <div className="profile-icon-container">
              <img src={XIcon} alt="X" className="profile-social-icon" />
            </div>
            <input
              type="text"
              placeholder="X URL"
              value={userData.socialLinks.twitter}
              onChange={(e) => handleSocialLinkChange('twitter', e.target.value)}
              className="profile-social-input"
            />
          </div>
          <div className="profile-social-row">
            <div className="profile-icon-container">
              <FaInstagram color="#C13584" size={24} />
            </div>
            <input
              type="text"
              placeholder="Instagram URL"
              value={userData.socialLinks.instagram}
              onChange={(e) => handleSocialLinkChange('instagram', e.target.value)}
              className="profile-social-input"
            />
          </div>
          <div className="profile-social-row">
            <div className="profile-icon-container">
              <FaFacebook color="#4267B2" size={24} />
            </div>
            <input
              type="text"
              placeholder="Facebook URL"
              value={userData.socialLinks.facebook}
              onChange={(e) => handleSocialLinkChange('facebook', e.target.value)}
              className="profile-social-input"
            />
          </div>
          <div className="profile-social-row">
            <div className="profile-icon-container">
              <FaSoundcloud color="#FF5500" size={24} />
            </div>
            <input
              type="text"
              placeholder="SoundCloud URL"
              value={userData.socialLinks.soundcloud}
              onChange={(e) => handleSocialLinkChange('soundcloud', e.target.value)}
              className="profile-social-input"
            />
          </div>
          <div className="profile-social-row">
            <div className="profile-icon-container">
              <FaSpotify color="#1DB954" size={24} />
            </div>
            <input
              type="text"
              placeholder="Spotify URL"
              value={userData.socialLinks.spotify}
              onChange={(e) => handleSocialLinkChange('spotify', e.target.value)}
              className="profile-social-input"
            />
          </div>
          <div className="profile-social-row">
            <div className="profile-icon-container">
              <img src={BandlabIcon} alt="BandLab" className="profile-social-icon" />
            </div>
            <input
              type="text"
              placeholder="BandLab URL"
              value={userData.socialLinks.bandlab}
              onChange={(e) => handleSocialLinkChange('bandlab', e.target.value)}
              className="profile-social-input"
            />
          </div>
        </div>
        <form onSubmit={handleSubmit} className="profile-form">
          <div className="profile-form-group">
            <label>Artist Name</label>
            <input
              type="text"
              name="artistName"
              value={userData.artistName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="profile-form-group">
            <label>Bio</label>
            <textarea name="bio" value={userData.bio} onChange={handleChange} />
          </div>
          <div className="profile-form-group">
            <label>Profile Picture</label>
            <input type="file" onChange={handleFileChange} />
          </div>
          <div className="profile-form-group">
            <label>City</label>
            <input type="text" name="city" value={userData.city} onChange={handleChange} />
          </div>
          <div className="profile-form-group">
            <label>State</label>
            <input type="text" name="state" value={userData.state} onChange={handleChange} />
          </div>
          <div className="profile-form-group">
            <label>Genres</label>
            <div className="profile-genres-list">
              {genresList.map((genre) => (
                <button
                  type="button"
                  key={genre}
                  className={`profile-genre-button ${userData.genres.includes(genre) ? 'selected' : ''}`}
                  onClick={() => handleGenreClick(genre)}
                >
                  {genre}
                </button>
              ))}
            </div>
            <div className="profile-selected-genres">
              {userData.genres.map((genre) => (
                <span key={genre} className="profile-selected-genre">
                  {genre}
                  <button type="button" onClick={() => removeGenre(genre)} className="profile-remove-genre">
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
          <button type="submit" className="profile-update-button">Update Profile</button>
        </form>
        <div className="profile-subscription">
          <p>Subscription Plan: {plan === 'premium' ? 'Premium' : 'Freemium'}</p>
          <button
            onClick={handleManageSubscription}
            className="profile-subscription-button"
            disabled={submitting}
          >
            {submitting
              ? 'Processing...'
              : plan === 'premium'
              ? 'Manage Subscription'
              : 'Upgrade Subscription'}
          </button>
        </div>
        <div className='sign-out-btn-container'>
          <button onClick={handleLogout} className="profile-logout-button">Sign Out</button>
        </div>
        <div className='delete-account-btn-container'>
          <button
            onClick={confirmDeleteAccount}
            className="profile-delete-button"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Account'}
          </button>
        </div>
        <div className="profile-legal">
          <p>
            By signing up, you agree to our{' '}
            <span className="profile-legal-link" onClick={() => navigate('/terms-of-service')}>
              Terms of Service
            </span>
            ,{' '}
            <span className="profile-legal-link" onClick={() => navigate('/end-user-license-agreement')}>
              End User License Agreement
            </span>
            , and{' '}
            <span className="profile-legal-link" onClick={() => navigate('/privacy-policy')}>
              Privacy Policy
            </span>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default Profile;